import { z } from "zod";

export const analyticsOverviewDataSchema = z.object({
  totalRequests: z.number(),
  successRequests: z.number(),
  errorRequests: z.number(),
  clientErrorRequests: z.number(), // 4xx count
  serverErrorRequests: z.number(), // 5xx count
  errorRate: z.number(), // 0–100, all errors
  avgLatencyMs: z.number(),
  p95LatencyMs: z.number(),
  audioSecondsProcessed: z.number(),
  previousPeriod: z.object({
    totalRequests: z.number(),
    errorRate: z.number(),
    avgLatencyMs: z.number(),
    audioSecondsProcessed: z.number(),
  }),
});

export const analyticsTimeSeriesItemSchema = z.object({
  timestamp: z.string(), // ISO8601 bucket start
  requests: z.number(),
  errors: z.number(),
  avgLatencyMs: z.number(),
});

/** Endpoint aggregates retain average latency for older clients; percentile fields use milliseconds. */
export const analyticsEndpointItemSchema = z.object({
  endpoint: z.string(),
  method: z.string(), // HTTP verb e.g. "GET", "POST"
  requests: z.number(),
  errors: z.number(),
  clientErrors: z.number(), // 4xx
  serverErrors: z.number(), // 5xx
  errorRate: z.number(),
  avgLatencyMs: z.number(),
  p50LatencyMs: z.number().optional(),
  p95LatencyMs: z.number(),
  audioSecondsProcessed: z.number(),
});

export const analyticsApiKeyItemSchema = z.object({
  apiKeyId: z.string(),
  apiKeyName: z.string(),
  keyPreview: z.string(),
  requests: z.number(),
  errors: z.number(),
  clientErrors: z.number(), // 4xx
  serverErrors: z.number(), // 5xx
  errorRate: z.number(),
  avgLatencyMs: z.number(),
  audioSecondsProcessed: z.number(),
  lastUsedAt: z.string().nullable(),
});

export const analyticsRecentRequestItemSchema = z.object({
  requestId: z.string().optional(),
  timestamp: z.string(),
  method: z.string().optional(),
  path: z.string().optional(),
  statusCode: z.number(),
  durationMs: z.number().optional(),
  endpoint: z.string().optional(),
  apiKeyName: z.string().optional(),
});

export type AnalyticsOverviewData = z.infer<typeof analyticsOverviewDataSchema>;
export type AnalyticsTimeSeriesItem = z.infer<
  typeof analyticsTimeSeriesItemSchema
>;
export type AnalyticsEndpointItem = z.infer<typeof analyticsEndpointItemSchema>;
export type AnalyticsApiKeyItem = z.infer<typeof analyticsApiKeyItemSchema>;
export type AnalyticsRecentRequestItem = z.infer<
  typeof analyticsRecentRequestItemSchema
>;
